// Permission system — mirrors backend src/auth/permissions.js
// Sync this file when BE permissions.js changes.
// Exception: "mediafile.template.manage" is FE-only for now (no BE counterpart
// yet) — see the note on it in PERMISSION_MAP below.

export const ROLE_IDS = {
  MANAGER: 1,
  DOCTOR: 2,
  COUNTER: 3,
  ASSISTANT: 4,
  HEADCOUNTER: 5,
  STOCK_MANAGER: 6,
  STOCK_STAFF: 7,
  RDM: 101,
  C_LEVEL: 102,
  ACCOUNTANT: 103,
  PROCUREMENT: 104,
};

export const ROLE_KEYS = Object.fromEntries(
  Object.entries(ROLE_IDS).map(([k, v]) => [v, k])
);

// Role-name constants (key → key) for referring to roles by symbol instead of
// raw strings, e.g. ROLES.MANAGER === "MANAGER".
export const ROLES = Object.fromEntries(
  Object.keys(ROLE_IDS).map((k) => [k, k])
);

export const BRANCH_ROLE_KEYS = [
  "MANAGER",
  "DOCTOR",
  "COUNTER",
  "ASSISTANT",
  "HEADCOUNTER",
  "STOCK_MANAGER",
  "STOCK_STAFF",
];

export const CENTRAL_ROLE_KEYS = [
  "RDM",
  "C_LEVEL",
  "ACCOUNTANT",
  "PROCUREMENT",
];

export const ROLE_LABELS_TH = {
  MANAGER: "ผู้จัดการสาขา",
  DOCTOR: "แพทย์",
  COUNTER: "เคาน์เตอร์",
  ASSISTANT: "ผู้ช่วย",
  HEADCOUNTER: "หัวหน้าเคาน์เตอร์",
  STOCK_MANAGER: "ผู้จัดการสต๊อก",
  STOCK_STAFF: "พนักงานสต๊อก",
  RDM: "ผู้จัดการเขต (RDM)",
  C_LEVEL: "ผู้บริหารระดับสูง (C-Level)",
  ACCOUNTANT: "บัญชี",
  PROCUREMENT: "จัดซื้อ",
};

// Short Thai descriptions shown under each role in the assign/remove dialogs so
// the operator understands what a role grants before choosing it.
export const ROLE_DESCRIPTIONS_TH = {
  MANAGER: "ดูแลภาพรวมสาขา · จัดการพนักงานและตารางงาน · เข้าถึงรายงานสาขา",
  DOCTOR: "เปิดเคสคนไข้ · บันทึกผลตรวจและการรักษา",
  COUNTER: "รับลงทะเบียนคนไข้ · จัดการนัดหมายและการชำระเงิน",
  ASSISTANT: "ช่วยงานข้างเก้าอี้ · เตรียมเคสและอุปกรณ์",
  HEADCOUNTER: "หัวหน้าทีมเคาน์เตอร์ · ดูแลงานหน้าร้านทั้งหมด",
  STOCK_MANAGER: "จัดการคลังสินค้าของสาขา · อนุมัติการเบิกจ่าย",
  STOCK_STAFF: "เบิก-จ่ายและตรวจนับสต๊อกประจำวัน",
  RDM: "ผู้จัดการเขต — ดูแลหลายสาขาที่กำหนดขอบเขตไว้",
  C_LEVEL: "ผู้บริหารระดับสูง — สิทธิ์เต็มทั้งคลินิก",
  ACCOUNTANT: "เข้าถึงข้อมูลการเงินและบัญชีระดับคลินิก",
  PROCUREMENT: "ดูแลการจัดซื้อจัดจ้างระดับคลินิก",
};

// Role-specific consequences surfaced in the remove dialog ("what they lose").
// Generic enough to stay accurate without mirroring the full backend ACL.
export const ROLE_REMOVE_EFFECTS_TH = {
  MANAGER: ["ไม่สามารถเปิดรายงานของสาขานี้", "ไม่สามารถจัดการพนักงานและตารางงาน"],
  DOCTOR: ["ไม่สามารถเปิดเคสและบันทึกผลตรวจในสาขานี้"],
  COUNTER: ["ไม่สามารถลงทะเบียนคนไข้และจัดการนัดหมายในสาขานี้"],
  ASSISTANT: ["ไม่สามารถเข้าถึงงานข้างเก้าอี้ในสาขานี้"],
  HEADCOUNTER: ["ไม่สามารถดูแลงานหน้าร้านของสาขานี้"],
  STOCK_MANAGER: ["ไม่สามารถจัดการคลังและอนุมัติการเบิกจ่ายของสาขานี้"],
  STOCK_STAFF: ["ไม่สามารถเบิก-จ่ายสต๊อกของสาขานี้"],
  RDM: ["ไม่สามารถดูแลสาขาในขอบเขตที่เคยกำหนด"],
  ACCOUNTANT: ["ไม่สามารถเข้าถึงข้อมูลการเงินระดับคลินิก"],
  PROCUREMENT: ["ไม่สามารถดูแลการจัดซื้อระดับคลินิก"],
  C_LEVEL: ["ไม่สามารถเข้าถึงสิทธิ์ผู้บริหารระดับคลินิก"],
};

export const PERMISSIONS = {
  BRANCH_ROLE_ALL: "branch.role_all",
  BRANCH_ROLE_ALL_WITH_STOCK: "branch.role_all.with_stock",
  BRANCH_ROLE_MANAGER: "branch.role_manager",
  BRANCH_ROLE_HEADCOUNTER: "branch.role_headcounter",
  BRANCH_ROLE_COUNTER: "branch.role_counter",
  BRANCH_ROLE_DOCTOR: "branch.role_doctor",
  STOCK_ROLE_ALL: "stock.role_all",
  STOCK_ROLE_MANAGER: "stock.role_manager",
  STOCK_HQ_APPROVE: "stock.hq_approve",
  STOCK_VIEW_DETAIL: "stock.view_detail",
  MIXED_HEADCOUNTER_AND_STOCK: "mixed.headcounter_and_stock",
  MIXED_HEADCOUNTER_AND_STOCK_MANAGER: "mixed.headcounter_and_stock_manager",
  USER_VIEW_BRANCH: "user.view.branch",
  USER_VIEW_CLINIC: "user.view.clinic",
  USER_REMOVE_BRANCH: "user.remove.branch",
  USER_REMOVE_CLINIC: "user.remove.clinic",
  DASHBOARD_CLINIC_VIEW: "dashboard.clinic.view",
  DASHBOARD_BRANCH_VIEW: "dashboard.branch.view",
  DASHBOARD_TARGET_VIEW: "dashboard.target.view",
  DASHBOARD_TARGET_MANAGE: "dashboard.target.manage",
  REPORT_CLINIC_VIEW: "report.clinic.view",
  VOUCHER_VIEW: "voucher.view",
  VOUCHER_MANAGE: "voucher.manage",
  VOUCHER_ASSIGN: "voucher.assign",
  TIER_VIEW: "tier.view",
  TIER_MANAGE: "tier.manage",
  TIER_RECOMPUTE: "tier.recompute",
  TIER_OVERRIDE: "tier.override",
  MEDIAFILE_TEMPLATE_MANAGE: "mediafile.template.manage",
};

export const PERMISSION_MAP = {
  // Phase 0 — branch-scoped, mirrored for cross-checks
  "branch.role_all": [1, 2, 3, 4, 5],
  "branch.role_all.with_stock": [1, 2, 3, 4, 5, 6, 7],
  "branch.role_manager": [1],
  "branch.role_headcounter": [1, 5],
  "branch.role_counter": [1, 3, 5],
  "branch.role_doctor": [2],
  "stock.role_all": [6, 7],
  "stock.role_manager": [6],
  "stock.hq_approve": [104],
  "stock.view_detail": [6, 7, 104],
  "mixed.headcounter_and_stock": [1, 5, 6, 7],
  "mixed.headcounter_and_stock_manager": [1, 5, 6],

  // Clinic-scoped views / removals
  "user.view.branch": [1, 101, 102],
  // ACCOUNTANT (103) อ่านอย่างเดียว — ต้องใช้รายชื่อแพทย์ระดับคลินิกเพื่อทำจ่าย
  // ปุ่มเพิ่ม/ลบผู้ใช้ยังซ่อนอยู่: 103 ไม่อยู่ใน ASSIGNABLE และไม่มีใน user.remove.*
  "user.view.clinic": [101, 102, 103],
  "user.remove.branch": [1, 101, 102],
  "user.remove.clinic": [102],

  // Executive dashboard
  // clinic.view รวม RDM: หน้าภาพรวมเปิดให้ RDM ได้เพราะ BE กรอง payload
  // เหลือเฉพาะสาขาที่ได้รับมอบหมาย
  "dashboard.clinic.view": [101, 102],
  "dashboard.branch.view": [1, 101, 102],
  // target.view ไม่มี MANAGER: getTargets ตรวจสิทธิ์ด้วย authByClinicURL ซึ่งอ่านเฉพาะ
  // scope clinic_wide / branch_subset ส่วน MANAGER เป็น scope 'branch' จึงไม่มีวันผ่าน
  // MANAGER เห็นเป้าของสาขาตัวเองผ่าน revenueTarget ใน payload ของแดชบอร์ดสาขาแทน
  "dashboard.target.view": [101, 102],
  "dashboard.target.manage": [102],

  // Clinic income report roll-up: executives (102) + finance/accountant (103)
  "report.clinic.view": [102, 103],

  // บัตรกำนัล — mirrors BE src/auth/permissions.js VOUCHER exactly.
  // view mixes branch roles (MANAGER 1, HEADCOUNTER 5) with central roles
  // (RDM 101, C_LEVEL 102, ACCOUNTANT 103); manage is central-only, because
  // minting bearer codes redeemable for cash is higher-privilege than creating
  // a manual ส่วนลด. A branch MANAGER therefore reads batches but cannot create
  // one or flip its status — the BE returns 403 for exactly that.
  // voucher.redeem is intentionally absent: redemption is gated at the payment
  // screen by the BE on validate/save, and no FE element keys off it.
  "voucher.view": [1, 5, 101, 102, 103],
  "voucher.manage": [101, 102, 103],
  // voucher.assign includes MANAGER (1) where voucher.manage does not, and this
  // is the only voucher key that mixes branch with central roles. A bound code
  // is hard-rejected at the counter for anyone but its holder with NO override,
  // so re-pointing the card is the only remedy — it has to be reachable from a
  // branch. HEADCOUNTER (5) and COUNTER (3) are excluded on purpose: they may
  // redeem a card, but re-pointing a cash-bearing one is a supervisor action.
  "voucher.assign": [101, 102, 103, 1],

  // ระดับลูกค้า (Customer Tier) — mirrors BE src/auth/permissions.js TIER exactly.
  // tier.view กว้างโดยตั้งใจ: พนักงานหน้าเคาน์เตอร์ (บทบาทสาขาทุกตัว) ต้องเห็น badge
  // ระดับของคนไข้ตอนคิดเงินเพื่อใช้ส่วนลด แต่สิทธิ์นี้ซื้อได้แค่ endpoint ที่ scope ระดับ
  // สาขา (getPatientTier/recompute/override) เท่านั้น ส่วน endpoint scope คลินิก
  // (config/list/create/update/delete/report) authorize ด้วย authByClinicURL ซึ่งอ่าน
  // เฉพาะ scope clinic_wide/branch_subset — บทบาทสาขาจึงเข้าไม่ถึงแม้จะอยู่ใน tier.view
  // ก็ตาม กับดักเดียวกับ dashboard.target.view ด้านบน
  // tier.manage แคบกว่า voucher.manage: ตั้ง threshold คือกำหนดนโยบายส่วนลดทั้งคลินิก
  // จึงไม่มี RDM (101) แม้ RDM จะอยู่ใน voucher.manage — เพราะ RDM มี scope
  // branch_subset ขณะที่ tier config เป็น clinic-wide
  "tier.view": [1, 2, 3, 4, 5, 101, 102, 103],
  "tier.manage": [102, 103],
  "tier.recompute": [1, 5, 101, 102, 103],
  "tier.override": [102, 103],

  // แบบฟอร์มคลินิก (mediaFile category "Template"): เพิ่ม/ลบได้เฉพาะผู้จัดการสาขา
  // (1) และผู้บริหารระดับคลินิก RDM (101) / C_LEVEL (102) — คนอื่นยังเปิดแบบฟอร์ม
  // ไปเซ็นได้ตามปกติ แค่เพิ่ม/ลบไม่ได้
  // FE-only ณ ตอนนี้: endpoint mediaFile/getPresigned และ mediaFile/recycleBin
  // ฝั่ง dent-cloud-service ยังไม่เช็ค role จึงกันได้แค่ระดับ UI
  "mediafile.template.manage": [1, 101, 102],

  // Auto-generated user.assign.* — mirrors anti-escalation matrix
  "user.assign.MANAGER": [101, 102],
  "user.assign.DOCTOR": [1, 101, 102],
  "user.assign.COUNTER": [1, 101, 102],
  "user.assign.ASSISTANT": [1, 101, 102],
  "user.assign.HEADCOUNTER": [1, 101, 102],
  "user.assign.STOCK_MANAGER": [1, 101, 102],
  "user.assign.STOCK_STAFF": [1, 101, 102],
  "user.assign.RDM": [102],
  "user.assign.ACCOUNTANT": [102],
  "user.assign.PROCUREMENT": [102],
  // Server-locked. Bootstrap script seeds C_LEVEL; never assignable from UI.
  "user.assign.C_LEVEL": [],
};

// Anti-escalation matrix: which role keys can assign which targets.
export const ASSIGNABLE = {
  C_LEVEL: [
    "RDM",
    "ACCOUNTANT",
    "PROCUREMENT",
    "MANAGER",
    "DOCTOR",
    "COUNTER",
    "ASSISTANT",
    "HEADCOUNTER",
    "STOCK_MANAGER",
    "STOCK_STAFF",
  ],
  RDM: [
    "MANAGER",
    "DOCTOR",
    "COUNTER",
    "ASSISTANT",
    "HEADCOUNTER",
    "STOCK_MANAGER",
    "STOCK_STAFF",
  ],
  MANAGER: [
    "DOCTOR",
    "COUNTER",
    "ASSISTANT",
    "HEADCOUNTER",
    "STOCK_MANAGER",
    "STOCK_STAFF",
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// กฎ master data — FE เท่านั้น ไม่มีคู่ใน BE permissions.js
// อย่าย้ายไปรวมกับ PERMISSION_MAP ข้างบน เพราะไฟล์นั้นต้อง mirror BE ตรงตัว
// ส่วนกฎนี้เข้มกว่าที่ BE บังคับ (BE ปล่อยถึง BRANCH_ROLE_ALL)
// ─────────────────────────────────────────────────────────────────────────────

// central role ที่นับว่าผ่านครึ่งแรกของกฎ — RDM (101) ไม่นับ เพราะ scope เป็น
// branch_subset ไม่ใช่ clinic_wide
export const MASTERDATA_CENTRAL_ROLE_IDS = [
  ROLE_IDS.C_LEVEL,
  ROLE_IDS.ACCOUNTANT,
  ROLE_IDS.PROCUREMENT,
];

// branch role ที่นับว่าผ่านครึ่งหลังของกฎ
export const MASTERDATA_BRANCH_ROLE_IDS = [ROLE_IDS.MANAGER];
